import DataTableAdminOptions from './DataTableAdminOptions';

import type { ButtonProps } from '@mantine/core';
import { ActionIcon, Button, Menu, Tooltip } from '@mantine/core';
import { IconChartCohort, IconCheck, IconFilter } from '@tabler/icons-react';
import type { Table } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useAtom } from 'jotai';
import { adminUiHiddenAtom } from '~/atoms/atoms';
import type { RowDataWithId } from './DataTable';
import DataTableFilterControl from './DataTableFilterControl';
import { DataTableResultCount } from './DataTableResultCount';
import DataTableRowsSelected from './DataTableRowsSelected';
import DataTableSearchBox from './DataTableSearchBox';
import DataTableViewOptions from './DataTableViewOptions';

type ActionItem = {
  key: string;
  label: string;
  icon?: React.ElementType;
  side?: 'left' | 'right';
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  title?: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export type GroupByOption = {
  value: string;
  label: string;
};

export function DataTableHeader<TData extends RowDataWithId>({
  actions,
  setGroupBy,
  groupBy,
  groupByOptions,
  table,
  itemName,
  showAdminOptions = false,
  isLoading,
}: {
  actions?: ActionItem[];
  setGroupBy?: (value: string) => void;
  groupBy?: string;
  groupByOptions?: GroupByOption[];
  table: Table<TData>;
  itemName: string;
  showAdminOptions?: boolean;
  isLoading?: boolean;
}) {
  const { isAdmin } = useAuthData();
  const [adminUiHidden] = useAtom(adminUiHiddenAtom);
  const verifiedShowAdminOptions = isAdmin && !adminUiHidden && showAdminOptions;
  const [showFilter, setShowFilter] = useState<boolean>(false);

  const filterableColumns = table.getAllColumns().filter((column) => Boolean(column.columnDef.enableColumnFilter));
  const filteredColumns = useMemo(() => {
    return filterableColumns.filter((column) => column.getFilterValue() !== undefined);
  }, [filterableColumns]);

  const selectedRows = table.getFilteredSelectedRowModel().rows;

  return (
    <div className="px-2 pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {actions
            ?.filter((action) => action.side === 'left')
            .map((action) => (
              <Button
                key={action.key}
                color={action.color}
                variant={action.variant}
                size="compact-sm"
                title={action.title}
                leftSection={!!action.icon && <action.icon size={16} />}
                onClick={action.onClick}
                loading={action.loading}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          <DataTableSearchBox
            table={table}
            setShowFilter={setShowFilter}
          />
          <DataTableResultCount
            table={table}
            suffix={`${itemName}${table.getRowCount() === 1 ? '' : 's'}`}
            isLoading={isLoading}
          />
        </div>

        <div className="flex items-center gap-2">
          {selectedRows.length > 0 && <DataTableRowsSelected table={table} />}
          {actions
            ?.filter((action) => action.side === 'right')
            .map((action) => (
              <Button
                key={action.key}
                color={action.color}
                variant={action.variant}
                size="compact-sm"
                title={action.title}
                leftSection={!!action.icon && <action.icon size={16} />}
                onClick={action.onClick}
                loading={action.loading}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          {verifiedShowAdminOptions ?
            <DataTableAdminOptions />
          : null}
          {filterableColumns.length > 0 && (
            <Tooltip label="Filter">
              <ActionIcon
                size="sm"
                variant="light"
                disabled={filteredColumns.length > 0}
                color={showFilter ? 'blue' : 'gray'}
                onClick={() => setShowFilter((prev) => !prev)}
              >
                <IconFilter size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {!!setGroupBy && groupByOptions != null && groupByOptions.length > 0 && (
            <Menu
              position="bottom-end"
              withinPortal
            >
              <Menu.Target>
                <Tooltip label="Group by">
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color={groupBy ? 'blue' : 'gray'}
                  >
                    <IconChartCohort size={16} />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Group by</Menu.Label>
                {groupByOptions.map((option) => (
                  <Menu.Item
                    key={option.value}
                    rightSection={groupBy === option.value ? <IconCheck size={14} /> : undefined}
                    onClick={() => setGroupBy(groupBy === option.value ? '' : option.value)}
                  >
                    {option.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}

          <DataTableViewOptions table={table} />
        </div>
      </div>
      {!!(showFilter || filteredColumns.length > 0) && (
        <div className="mt-4 flex items-center gap-2">
          <DataTableFilterControl table={table} />{' '}
        </div>
      )}
    </div>
  );
}
